import { createWebHistory, createRouter } from "vue-router";
import axios from 'axios';
import routes from './routes';
import { API_BASE_URL } from '@/config/api';

// Add csrfPreventionToken to all write requests
axios.interceptors.request.use((config) => {
  const method = (config.method || '').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const token = store.getters['auth/csrfPreventionToken'];
    if (token) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Prevention-Token'] = token;
    }
  }
  return config;
});
import appConfig from "../../app.config";
import store from '@/state/store';

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

let handlingUnauthorized = false;

function isAuthorizationError(error) {
  const status = Number(error?.response?.status || 0);
  if (status === 401 || status === 403) return true;

  const responseMessage = String(error?.response?.data?.message || '').toLowerCase();
  const errorMessage = String(error?.message || '').toLowerCase();
  const combined = `${responseMessage} ${errorMessage}`;

  return combined.includes('not authorized')
    || combined.includes('unauthorized')
    || combined.includes('forbidden')
    || combined.includes('permission denied')
    || combined.includes('nicht autorisiert');
}

function isAuthEndpoint(url) {
  const normalized = String(url || '').toLowerCase();
  // /user/password liefert 401 bei falschem bestehendem Passwort, nicht bei abgelaufener Session.
  return normalized.includes('/user/login')
    || normalized.includes('/user/logout')
    || normalized.includes('/user/password');
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = String(error?.config?.url || '');
    const isAuthorizedSession = store.getters['auth/isApiAuthenticated'];

    if (
      isAuthorizedSession
      && !handlingUnauthorized
      && !isAuthEndpoint(requestUrl)
      && !error?.config?.skipAuthLogout
      && isAuthorizationError(error)
    ) {
      handlingUnauthorized = true;

      try {
        store.dispatch('auth/clearApiSession');
        store.dispatch('virtualInfrastructure/clearProxmoxClusters');
        store.commit('auth/SET_CURRENT_USER', null);

        const currentPath = router.currentRoute?.value?.fullPath || '/';
        const redirectQuery = currentPath && currentPath !== '/login'
          ? { redirectFrom: currentPath }
          : {};

        if (router.currentRoute?.value?.name !== 'login') {
          await router.replace({ name: 'login', query: redirectQuery });
        }
      } finally {
        handlingUnauthorized = false;
      }
    }

    return Promise.reject(error);
  }
);

// API based Authentication
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  
  if (!authRequired) return next();
  
  const csrfToken = store.getters['auth/csrfPreventionToken'];
  if (!csrfToken) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }
  await axios.get(`${API_BASE_URL}/user/profile`, {
    headers: { 'X-CSRF-Prevention-Token': csrfToken },
    withCredentials: true,
  }).then((data) => {
    const profileUser = data.data.user || null;
    store.dispatch('auth/setApiCurrentUser', profileUser);
    store.dispatch('auth/applyProfileAccessFlags', profileUser);

    const requiresVmAccess = routeTo.matched.some((route) => route.meta.requiresVmAccess);
    const requiresKubernetesAccess = routeTo.matched.some((route) => route.meta.requiresKubernetesAccess);
    const requiresSdnAccess = routeTo.matched.some((route) => route.meta.requiresSdnAccess);
    const requiresFinancialAccess = routeTo.matched.some((route) => route.meta.requiresFinancialAccess);
    const requiresAdminAccess = routeTo.matched.some((route) => route.meta.requiresAdminAccess);

    if (requiresVmAccess && !store.getters['auth/canAccessVm']) {
      return next({ name: 'default' });
    }
    if (requiresKubernetesAccess && !store.getters['auth/canAccessKubernetes']) {
      return next({ name: 'default' });
    }
    if (requiresSdnAccess && !store.getters['auth/canAccessAdmin']) {
      return next({ name: 'default' });
    }
    if (requiresFinancialAccess && !store.getters['auth/canAccessFinancial']) {
      return next({ name: 'default' });
    }
    if (requiresAdminAccess && !store.getters['auth/canAccessAdmin']) {
      return next({ name: 'default' });
    }

    next();
  }).catch(() => {
    store.dispatch('auth/clearApiSession');
    store.dispatch('virtualInfrastructure/clearProxmoxClusters');
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  });
});

router.beforeEach((routeTo, routeFrom, next) => {
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {

    // Check if auth is required on this route
    // (including nested routes).
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

    // If auth isn't required for the route, just continue.
    if (!authRequired) return next();

    // If auth is required and the user is logged in...
    if (store.getters['auth/loggedIn']) {
      // Validate the local user token...
      return store.dispatch('auth/validate').then((validUser) => {
        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        validUser ? next() : redirectToLogin();
      });
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin();

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
    }
  } else {
    const publicPages = ['/login', '/register', '/forgot-password'];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = store.getters['auth/isApiAuthenticated'];

    if (authpage && !loggeduser) {
      return next('/login');
    }

    next();
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
